import React from "react";
import { useAccount } from "wagmi";
import { useTranslation } from "next-i18next";
import { Flex, FlexProps, HStack, Stack } from "@chakra-ui/react";
import { dialogRadius } from "@looksrare/chakra-theme";
import { formatToSignificant } from "@looksrare/utils";
import { EthTokenIcon, LooksTokenIcon } from "@looksrare/uikit";
import { Text } from "uikit";
import { ConnectWalletGuard } from "components/Layout/ConnectWalletGuard";
import { useUnclaimedRafflePrizes } from "views/win/userGeneratedRaffles/utils/hooks";
import { parseUnclaimedPrizes } from "views/win/userGeneratedRaffles/utils/unclaimedPrizes";
import { Raffle } from "views/win/userGeneratedRaffles/types";
import { ClaimPrizesButton } from "./ClaimPrizesButton";

interface UnclaimedWinningsProps extends FlexProps {
  raffleOnChainId?: Raffle["raffleOnChainId"];
  raffleVersion?: Raffle["version"];
}
export const UnclaimedWinnings = ({ raffleOnChainId, raffleVersion, ...props }: UnclaimedWinningsProps) => {
  const { t } = useTranslation();
  const { address } = useAccount();

  /**
   * Fetch the unclaimed prizes. If raffleOnChainId is not provided, we're fetching
   * all prizes for the connected user, both v1 and v2.
   */
  const { data: unclaimedPrizes } = useUnclaimedRafflePrizes(
    { address: address!, raffleOnChainId, raffleVersion },
    { enabled: !!address }
  );

  const { tokens, ethAmount, looksAmount } = parseUnclaimedPrizes(unclaimedPrizes || []);
  const isNoPrizes = !unclaimedPrizes?.length;

  const prizesElement = (() => {
    if (!address) {
      return (
        <Text bold color="text-disabled">
          —
        </Text>
      );
    }

    if (isNoPrizes) {
      return (
        <Text bold color="text-disabled">
          {t("{{count}} NFTs", { count: 0 })}
        </Text>
      );
    }

    return (
      <HStack
        spacing={1}
        divider={
          <Text bold color="text-03" px={1}>
            /
          </Text>
        }
      >
        {!!tokens.length && (
          <Text bold color="text-01">
            {t("{{count}} NFTs", { count: tokens.length })}
          </Text>
        )}
        {!!ethAmount && (
          <Flex alignItems="center" gap={1}>
            <EthTokenIcon boxSize={4} />
            <Text bold color="text-01">
              {formatToSignificant(ethAmount)}
            </Text>
          </Flex>
        )}
        {!!looksAmount && (
          <Flex alignItems="center" gap={1}>
            <LooksTokenIcon boxSize={4} />
            <Text bold color="text-01">
              {formatToSignificant(looksAmount)}
            </Text>
          </Flex>
        )}
      </HStack>
    );
  })();

  return (
    <Flex bg="interactive-01" p="1px" borderRadius="dialog" {...props}>
      <Flex
        borderRadius={`${dialogRadius - 1}px`}
        p={4}
        gap={3}
        bgGradient="linear(to-b, ui-gradient-02-a, ui-gradient-02-b)"
        width="100%"
      >
        <Stack spacing={2} flexGrow={1}>
          <Text color="text-03" textStyle="detail" bold>
            {t("Your Unclaimed Winnings")}
          </Text>
          {prizesElement}
        </Stack>
        <Flex alignItems="center" justifyContent="flex-end" height="100%" flexBasis="none">
          <ConnectWalletGuard>
            {/* The button component contains a transaction modal, do not unmount the Button */}
            <ClaimPrizesButton unclaimedPrizes={unclaimedPrizes || []} isDisabled={!unclaimedPrizes?.length}>
              {!unclaimedPrizes?.length ? "Nothing to Claim" : undefined}
            </ClaimPrizesButton>
          </ConnectWalletGuard>
        </Flex>
      </Flex>
    </Flex>
  );
};
